import { useState } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader, card, btnPrimary, colors } from '../components/ui'
import { useNotificationList } from '../hooks/use-notifications'
import type { NotificationSeverity } from '@alpha-edge/shared'

const SEVERITY_COLORS: Record<NotificationSeverity, string> = {
  critical: '#dc2626',
  warning: '#f59e0b',
  info: '#9ca3af',
}

export function PreferencesNotificationsPage() {
  const [onlyUnread, setOnlyUnread] = useState(false)
  const { rows, unreadCount, loading, loadMore, hasMore, markAllRead } = useNotificationList({ onlyUnread })

  return (
    <div>
      <PageHeader title="Notifications" subtitle="Full history of bell alerts" />
      <div style={card}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
          }}
        >
          <label
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', cursor: 'pointer' }}
          >
            <input
              type="checkbox"
              checked={onlyUnread}
              onChange={(e) => setOnlyUnread(e.target.checked)}
              data-testid="only-unread-toggle"
            />
            Show only unread
          </label>
          <button
            style={{ ...btnPrimary, fontSize: '0.875rem' }}
            disabled={unreadCount === 0}
            onClick={() => void markAllRead()}
            data-testid="mark-all-read"
          >
            Mark all read
          </button>
        </div>

        {loading && rows.length === 0 ? (
          <div style={{ color: colors.textMuted, fontSize: '0.875rem' }}>Loading...</div>
        ) : rows.length === 0 ? (
          <div style={{ color: colors.textMuted, fontSize: '0.875rem' }}>No notifications yet.</div>
        ) : (
          <>
            {rows.map((row) => (
              <div
                key={row.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.75rem',
                  padding: '0.75rem 0',
                  borderBottom: `1px solid ${colors.border}`,
                  ...(row.readAt === null
                    ? {
                        borderLeft: `3px solid ${SEVERITY_COLORS[row.severity]}`,
                        paddingLeft: '0.75rem',
                      }
                    : {}),
                }}
              >
                <span
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: SEVERITY_COLORS[row.severity],
                    flexShrink: 0,
                    marginTop: '5px',
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{row.title}</div>
                  {row.body && (
                    <div style={{ color: colors.textMuted, fontSize: '0.8125rem', marginTop: '2px' }}>{row.body}</div>
                  )}
                  <div style={{ color: colors.textMuted, fontSize: '0.75rem', marginTop: '4px' }}>
                    {new Date(row.createdAt).toLocaleString()}
                  </div>
                </div>
                {row.link && (
                  <Link
                    to={row.link}
                    style={{
                      color: colors.accent,
                      fontSize: '0.8125rem',
                      textDecoration: 'underline',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    View →
                  </Link>
                )}
              </div>
            ))}
            {hasMore && (
              <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                <button
                  style={{ ...btnPrimary }}
                  disabled={loading}
                  onClick={() => void loadMore()}
                  data-testid="load-more"
                >
                  {loading ? 'Loading...' : 'Load more'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
